/**
 * Component Registry for Weidner Apartment Homes Rise Theme
 * 
 * Extends the base rise theme registry with Weidner Apartment Homes Rise specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-rise/registry';
import { AccessibilityStatement } from './components/AccessibilityStatement';
import { ContactForm } from './components/ContactForm';
import { Footer } from './components/Footer';
import { FutureResidentsClub } from './components/FutureResidentsClub';
import { GallerySeparator } from './components/GallerySeparator';
import { Header } from './components/Header';
import { HeroSolidBG } from './components/HeroSolidBG';
import { PetPolicy } from './components/PetPolicy';
import { PricingVerbiage } from './components/PricingVerbiage';

/**
 * Weidner Apartment Homes Rise specific component overrides.
 * These components have been customized for the Weidner Apartment Homes Rise brand.
 *
 * - `Footer`: re-synced verbatim with the rise base, with only the
 *   Weidner-only "Text Us!" link added between the Phone and Address
 *   blocks in the contact column. The link binds to the per-property
 *   `custom_text_us_url` field and is suppressed when no URL is set, so
 *   properties without an SMS landing page don't render a dead row.
 *   The rest of the footer (phone styling, "Professionally Managed by"
 *   label, contrast-aware logo selection, attribution row, legal nav,
 *   etc.) mirrors the parent — edits made upstream in rise are reflected
 *   here automatically as long as the fork stays in sync. See the header
 *   comment in `src/components/Footer.tsx` for the maintenance checklist.
 * - `Header`: forked from the rise base to surface the Home page in the
 *   main navigation. The rise base intentionally hides `home` from the
 *   dynamic nav (the logo serves as the home affordance), but Weidner
 *   wants an explicit "Home" link alongside the other top-level pages.
 *   The fork drops `'home'` from `NAV_EXCLUDED_ALIASES`, merges local
 *   `src/pages.json` overrides with the rise page list, and groups
 *   related pages into Weidner-specific dropdown labels.
 * - `FutureResidentsClub`: Weidner-specific replacement for the rise
 *   base implementation. Same slot name, different content/structure:
 *   a six-card "Join Our Community" benefits grid (Pre-Approved, First
 *   Choice, Priority, Commitment Free, Get to Know Us, Joining Is Easy)
 *   followed by a two-column "How It Works" section with editable copy,
 *   CTA, and image. Wired into the `future-residents-club` page in
 *   `src/pages.json`.
 * - `HeroSolidBG`: mirrors the rise base verbatim except that the inner
 *   flex container's top padding on mobile and tablet is bumped so the
 *   title gets more breathing room above it on those viewports. Parent
 *   uses `pt-6 md:pt-4 lg:pt-16` (mobile 1.5rem, tablet 1rem, desktop
 *   4rem); the fork uses `pt-12 md:pt-12 lg:pt-16` so mobile and tablet
 *   both sit at 3rem and desktop stays at 4rem. See the header comment
 *   in `src/components/HeroSolidBG.tsx` for the maintenance checklist.
 * - `PetPolicy`: mirrors the rise base verbatim except that the bulleted
 *   list inside the Pet Restrictions box renders right-pointing arrow
 *   markers (▸) in the primary color instead of disc bullets, with each
 *   marker absolutely positioned in a 1.25rem gutter so it sits on the
 *   same baseline as the item text to its right. See the header comment
 *   in `src/components/PetPolicy.tsx` for the maintenance checklist.
 * - `AccessibilityStatement`: mirrors the rise base except that the
 *   embedded AudioEye portal iframe is removed. On Weidner domains the
 *   portal renders an "AudioEye is currently inactive on this website"
 *   notice, so the iframe (and the `property_domain` lookup that fed its
 *   src) is dropped; the sr-only heading, rich-text statement body, and
 *   editable "Contact Us" link are unchanged. See the header comment in
 *   `src/components/AccessibilityStatement.tsx` for the maintenance
 *   checklist.
 * - `ContactForm`: mirrors the rise base except that the opening parallax
 *   background image section is removed. The lead form now sits centered
 *   on the page's default background (no bg image, no dark overlay, no
 *   parallax scroll handler). The horizontal contact-info row beneath the
 *   form is unchanged. See the header comment in
 *   `src/components/ContactForm.tsx` for the maintenance checklist.
 * - `GallerySeparator`: mirrors the rise base except that the headline
 *   column no longer overlaps the featured image on the right. The
 *   parent pulls the image ~6rem into the title column (and paints a
 *   left-edge gradient on the image so the overlapping text stays
 *   legible); the fork removes the negative margin, adds a small gutter
 *   between the columns, and drops the now-unneeded gradient. See the
 *   header comment in `src/components/GallerySeparator.tsx` for the
 *   maintenance checklist.
 * - `PricingVerbiage`: adapted from the preferred theme. Renders
 *   `custom_pricing_verbiage` as centered muted richtext above the
 *   ApartmentSync floor-plan embed. Hidden when the field is empty.
 */
const weidnerapartmenthomesriseOverrides: Partial<ComponentRegistry> = {
  AccessibilityStatement,
  ContactForm,
  Footer,
  FutureResidentsClub,
  GallerySeparator,
  Header,
  HeroSolidBG,
  PetPolicy,
  PricingVerbiage,
};

/**
 * Final merged registry for Weidner Apartment Homes Rise properties.
 * Inherits all components from the rise theme, with Weidner Apartment Homes Rise overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...weidnerapartmenthomesriseOverrides,
};

export default registry;
